import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { LogOut, Music, FileText, Users, ExternalLink, Loader2, MessageCircle } from 'lucide-react';
import { useAdminAuth } from '../../lib/AdminAuth';
import { Button } from '../ui/button';
import ReleasesManager from './ReleasesManager';
import BlogManager from './BlogManager';
import ArtistsManager from './ArtistsManager';
import ContactManager from './ContactManager';

const AdminDashboard = () => {
  const { user, loading, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('releases');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#E9C46A] animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/admin/login" replace />;

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-lg border-b border-[#D4A94F]/15">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-werdi-media.png" alt="Werdi Media" className="w-10 h-10 object-contain" />
            <div>
              <p className="text-white font-bold text-sm leading-tight">Werdi Media CMS</p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-[#D4A94F] font-medium">
                Content Management
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-neutral-400 hover:text-[#E9C46A] link-transition flex items-center gap-1.5"
            >
              Lihat Website <ExternalLink className="w-3 h-3" />
            </a>
            <span className="text-neutral-600">|</span>
            <span className="text-sm text-neutral-300">
              <span className="text-neutral-500">admin:</span> {user.username}
            </span>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="text-neutral-400 hover:text-red-400 rounded-full"
            >
              <LogOut className="w-4 h-4 mr-1" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        {/* Tab Nav */}
        <div className="flex gap-2 mb-8 border-b border-white/[0.06]">
          <TabButton active={tab === 'releases'} onClick={() => setTab('releases')} icon={Music}>
            Rilisan
          </TabButton>
          <TabButton active={tab === 'artists'} onClick={() => setTab('artists')} icon={Users}>
            Artis
          </TabButton>
          <TabButton active={tab === 'blog'} onClick={() => setTab('blog')} icon={FileText}>
            Blog & Artikel
          </TabButton>
          <TabButton active={tab === 'contact'} onClick={() => setTab('contact')} icon={MessageCircle}>
            Pesan Kontak
          </TabButton>
        </div>

        {tab === 'releases' && <ReleasesManager />}
        {tab === 'artists' && <ArtistsManager />}
        {tab === 'blog' && <BlogManager />}
        {tab === 'contact' && <ContactManager />}
      </main>
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, children }) => (
  <button
    onClick={onClick}
    className={`px-5 py-3 -mb-px flex items-center gap-2 font-semibold text-sm border-b-2 btn-transition ${
      active
        ? 'border-[#D4A94F] text-[#E9C46A]'
        : 'border-transparent text-neutral-500 hover:text-neutral-300'
    }`}
  >
    <Icon className="w-4 h-4" />
    {children}
  </button>
);

export default AdminDashboard;
